/**
 * 手入力取引閲覧モーダル (Feature D、 spec `bilateral-trade-manual-detail.md`)。
 *
 * 手入力取引 (IsManual=true) の相対取引契約を **read-only** で表示するモーダル。
 * AC-002: 全項目 read-only (編集不可・保存ボタンなし・削除ボタンなし)。
 * 編集モードへの切替導線は対象外 (Feature C / issue #1251 の領分)。
 *
 * データ源: `useGetBilateralTrade(id)` の `BilateralTrade` (full 情報)。 loading / error / data を内部で出し分ける (= RecapModal と同パターン)。
 *
 * design 梅版 `bilateral.html` の `#bi-detail-modal` (bi-mode-detail) を app 実装に
 * 移植。 mock-app 準拠化 (設計書 §5 出力側) で 約定日 / 商品形態 / 精算単価参照エリア /
 * 受給日カレンダー / 契約単価 / 自社BG / 取引先BG / 受給時間(h) / 電力量(kWh) /
 * leg 展開を表示対象に追加した。
 *
 * AC-SBM-007: 受渡期間セクションのカレンダービュー (`DeliveryCalendarView`) で
 * 対象日を時間帯区分ごとに色分けし、受渡カレンダー例外 (適用日 / 除外日) を
 * 重ねて表示する。 契約情報セクションの「カレンダー例外」テキスト羅列行は削除済み
 * (issue #5905、2026-08-24 PdM 判断)。 色分けの計算は GetBilateralTrade の
 * `calendarDays` (backend 計算済み、issue #7965) をそのまま使い、frontend では
 * 再計算しない。
 *
 * **燃調 (適用フラグ / プロファイル) は表示しない** — 移植対象外 (設計書 AC-11 /
 * AC-SBM-003 の一部不採用、追跡 #4580)。
 *
 * **オプション取引セクション・利用率(下限-上限) も P0 では表示しない** (#4801 PdM 確定
 * 「燃調系・オプション表記は収支管理・通告変更管理が入るまで不要」/ #4787 / spec
 * `bilateral-trade-list.md` Discussion 決定「オプション有無・利用率・通告タイミングの
 * 表示はすべて通告変更管理画面で扱い本画面から撤去する。優先度 P3」)。
 * TODO(#4801): P1 で該当セクション・行を戻す (i18n key は残置済み)。
 */
import type { MouseEvent, ReactNode } from 'react'

import type {
  BilateralTrade,
  DeliveryCalendarDay,
} from '@ebalance/proto/bffuser/v1/supply_bilateral_trade_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  Flex,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { Modal } from '~/components/common/Modal'
import { RegionLoading } from '~/components/common/RegionLoading'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { useListBusinessMasters } from '~/hooks/businessMaster/useListBusinessMasters'
import {
  AreaCodeLabel,
  DeliveryCalendarKeyLabel,
  DeliveryTypeBadge,
  ExecutionRouteBadge,
  LegTimeZoneLabel,
  ProductTypeBadge,
  StatusBadge,
} from '~/pages/SupplyBilateral/components/Badges'
import { DeliveryCalendarView } from '~/pages/SupplyBilateral/components/ManualTradeDetailModal/DeliveryCalendarView'
import { LegDetailTable } from '~/pages/SupplyBilateral/components/ManualTradeDetailModal/LegDetailTable'
import {
  decimalString,
  formatExecutionDate,
  formatPartnerWithCode,
  periodDaysInclusive,
} from '~/pages/SupplyBilateral/models/formatters'

type Props = {
  isOpen: boolean
  onClose: () => void
  /** 表示対象取引の full 情報。 fetch 中 / エラー時は undefined。 */
  trade?: BilateralTrade | undefined
  /** 受渡期間内の日ごとのカレンダービュー状態 (AC-SBM-007、backend 計算済み)。
   *  trade 未取得時と同様、 fetch 中 / エラー時は undefined。 */
  calendarDays?: DeliveryCalendarDay[] | undefined
  /** データ取得中 */
  isLoading?: boolean
  /** データ取得エラー */
  isError?: boolean
  /** 「この契約の 48点データを見る」 押下時のコールバック (= 48点データ画面へ遷移)。 */
  onGoTo48?: ((event?: MouseEvent) => void) | undefined
}

/** 代替表示文字 (= 未設定値の placeholder)。 */
const FALLBACK = '—'

// 利用率 (0-1 スケール Decimal を「N%」表示にする変換) は本画面のスコープ外
// (通告変更管理 P3 へ移管。 spec bilateral-trade-recap.md AC-SBR-008〜010 欠番)。

type DetailRowProps = {
  label: string
  value: ReactNode
}

/**
 * 2 列詳細テーブルの 1 行 (ラベル列 + 値列)。 RecapModal と同じ視覚規約:
 * ラベル列は固定幅 + Surface.tableHeader 背景 + Text.midEmphasis (TH 相当)。
 */
const DetailRow = ({ label, value }: DetailRowProps): React.ReactElement => (
  <Flex borderColor="Border.lowEmphasis" borderBottomWidth={1}>
    <Box
      flexShrink={0}
      width="180px"
      background="Surface.tableHeader"
      paddingX={3}
      paddingY={1.5}
    >
      {/* 8080 `.bi-recap-table th` 相当の行見出し。 md/semibold/mid-emphasis で、
          同じモーダル内の `LegDetailTable` の TH ([[TABLE_HEADER_CELL_STYLE]]) と
          太さ・字送りを揃える (2026-09-17 デザイナー指摘「TH が太字であるべき」)。
          旧 `smallText` は **sm (12px) で weight を持たない** role だったため、
          14px/600 の見出しに対して 12px/継承 で描かれていた。
          [design 差異] 8080 の `.bi-recap-table th` は font-weight 500 だが、
          これは bilateral.html の画面ローカル class。 DS canonical の
          `.ecl-table th` は 600 で、同モーダルの leg 表も 600。 1 画面で見出しの
          太さが 2 種類になるのを避け、 canonical 側に寄せる。 */}
      <Text textStyle="tableHeader" color="Text.midEmphasis">
        {label}
      </Text>
    </Box>
    <Box flex={1} paddingX={3} paddingY={1.5}>
      {typeof value === 'string' ? (
        <Text textStyle="tableCell">{value}</Text>
      ) : (
        value
      )}
    </Box>
  </Flex>
)

/** セクション見出し。 */
const SectionTitle = ({
  children,
}: {
  children: ReactNode
}): React.ReactElement => (
  <Box paddingTop={3} paddingBottom={1} paddingX={1}>
    <Text textStyle="sectionTitle">{children}</Text>
  </Box>
)

/**
 * 手入力取引閲覧モーダル。 loading / error / data を内部で出し分ける。
 */
export const ManualTradeDetailModal = ({
  isOpen,
  onClose,
  trade,
  calendarDays,
  isLoading = false,
  isError = false,
  onGoTo48,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  // modal footer のボタンは **DS 既定サイズ (md / 40px)**。`size="sm"` を当てない
  // (2026-09-16 デザイナー指摘「modal のボタンも DS 違反」)。
  // 共通モーダル (`ConfirmDialog` / `DeleteCaseDialog`) はいずれも size を渡さず
  // 既定のままで、design も `supply/bilateral.html:555-559` の
  // `class="btn btn-tertiary"` に `btn-sm` を付けていない。本 modal だけが sm だった。
  const footer = (
    <Flex gap={2}>
      {onGoTo48 !== undefined && trade !== undefined && (
        <TertiaryButton onClick={(e?: MouseEvent) => onGoTo48(e)}>
          {t('page:supplyBilateral.manualDetail.goTo48')}
        </TertiaryButton>
      )}
      <TertiaryButton onClick={onClose}>
        {t('page:supplyBilateral.manualDetail.close')}
      </TertiaryButton>
    </Flex>
  )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      // 8080 `#bi-detail-modal` = `ecl-modal--xl` (UI 辞書 screens/bilateral-trade.md 4)
      size="xl"
      title={t('page:supplyBilateral.manualDetail.title')}
    >
      {isLoading && <RegionLoading isLoading />}

      {!isLoading && isError && (
        <Alert
          description={t(
            'page:supplyBilateral.manualDetail.error.fetchFailedDescription',
          )}
          status="error"
          title={t('page:supplyBilateral.manualDetail.error.fetchFailedTitle')}
        />
      )}

      {!isLoading && !isError && trade !== undefined && (
        <ManualTradeDetailBody
          calendarDays={calendarDays ?? []}
          trade={trade}
        />
      )}
    </Modal>
  )
}

type BodyProps = {
  trade: BilateralTrade
  calendarDays: DeliveryCalendarDay[]
}

/** 詳細本体 (= セクション + 2 列詳細テーブル)。 */
const ManualTradeDetailBody = ({
  trade,
  calendarDays,
}: BodyProps): React.ReactElement => {
  const { t } = useTranslation()
  const label = (key: string): string =>
    t(`page:supplyBilateral.manualDetail.label.${key}`)

  // 自社側事業者コードに事業者名を併記する (spec `bilateral-trade-manual-detail.md`
  // AC-SBM-003 ※、 #5904 2026-08-24 PdM 判断)。 事業者名は事業者マスタから引く。
  // 取得中・取得失敗・該当なしはコードだけを出す (名前は補助なので、 取れないことを
  // 理由に詳細を止めない)。
  const { data: businessMasters } = useListBusinessMasters({
    throwOnError: false,
  })
  const ownBusinessName = businessMasters?.items.find(
    (item) => item.businessCode === trade.ownBusinessCd,
  )?.businessName

  // #6236 (Design Decision 2) で旧 legs[0] のフィールドは BilateralTrade
  // 直下へ昇格済み (flat 化)。
  const volumeStr = decimalString(trade.volumeMw)
  const unitPriceStr = decimalString(trade.unitPriceYenPerKwh)
  const supplyHoursStr = decimalString(trade.supplyHours)
  const energyKwhStr = decimalString(trade.energyKwh)
  const periodDays = periodDaysInclusive(
    trade.deliveryPeriodFrom,
    trade.deliveryPeriodTo,
  )

  // オプション・利用率の派生値 (trade.option / 利用率の % 表示 / 通告タイミング /
  //   基本料金) は本画面のスコープ外 (通告変更管理 P3 へ移管。 spec
  //   bilateral-trade-recap.md AC-SBR-008〜010 欠番)。

  return (
    <Box>
      {/* ── 基本情報 ── */}
      <SectionTitle>
        {t('page:supplyBilateral.manualDetail.section.basic')}
      </SectionTitle>
      <Box borderColor="Border.lowEmphasis" borderTopWidth={1}>
        <DetailRow
          label={label('executionRoute')}
          value={<ExecutionRouteBadge route={trade.executionRoute} />}
        />
        <DetailRow
          label={label('executionDate')}
          value={
            trade.executionDate !== undefined
              ? formatExecutionDate(trade.executionDate)
              : FALLBACK
          }
        />
        <DetailRow
          label={label('productType')}
          value={<ProductTypeBadge productType={trade.productType} />}
        />
        <DetailRow label={label('publicTradeId')} value={trade.publicTradeId} />
        {trade.dealId !== '' && (
          <DetailRow label={label('dealId')} value={trade.dealId} />
        )}
        <DetailRow
          label={label('status')}
          value={<StatusBadge status={trade.status} />}
        />
        <DetailRow
          label={label('area')}
          value={<AreaCodeLabel areaCode={trade.areaCode} />}
        />
        {/* 自社側事業者コード (OCCTO 4 桁)。 spec `bilateral-trade-manual-detail.md`
            Discussion 決定 A (2026-08-17 PdM 判断): CSV アップロード側で事業者コードが
            必須列になったため、 個別入力との等価性を保つ目的で基本情報に read-only 表示
            する。 design `supply/bilateral.html` は monospace の <code> 表示だが、 同じく
            design で <code> 表示の 自社BG / 取引先BG 行が app 実装では plain text のため、
            同一テーブル内の一貫性を優先して既存行に揃える。 */}
        <DetailRow
          label={label('ownBusinessCd')}
          value={
            trade.ownBusinessCd === ''
              ? FALLBACK
              : // spec の例は全角括弧「OWN003（○○電力）」だが、 表記は
                // `conventions.md`「括弧の前は半角スペース」 に揃える (画面 spec より規約を優先)。
                ownBusinessName !== undefined && ownBusinessName !== ''
                ? `${trade.ownBusinessCd} (${ownBusinessName})`
                : trade.ownBusinessCd
          }
        />
        <DetailRow
          label={label('partner')}
          value={
            trade.partnerName === ''
              ? FALLBACK
              : formatPartnerWithCode(
                  trade.partnerName,
                  trade.partnerSubmissionEntityCode,
                )
          }
        />
        <DetailRow
          label={label('buySell')}
          value={<TradeSideLabel side={trade.buySell} />}
        />
        <DetailRow
          label={label('deliveryType')}
          value={<DeliveryTypeBadge deliveryType={trade.deliveryType} />}
        />
        <DetailRow
          label={label('volumeMw')}
          value={volumeStr === '' ? FALLBACK : `${volumeStr} MW`}
        />
      </Box>

      {/* ── 受渡期間 ── */}
      <SectionTitle>
        {t('page:supplyBilateral.manualDetail.section.deliveryPeriod')}
      </SectionTitle>
      <Box borderColor="Border.lowEmphasis" borderTopWidth={1}>
        <DetailRow
          label={label('periodFrom')}
          value={
            trade.deliveryPeriodFrom !== undefined
              ? formatExecutionDate(trade.deliveryPeriodFrom)
              : FALLBACK
          }
        />
        <DetailRow
          label={label('periodTo')}
          value={
            trade.deliveryPeriodTo !== undefined
              ? formatExecutionDate(trade.deliveryPeriodTo)
              : FALLBACK
          }
        />
        {/* 時間帯区分は一覧 (`bilateral-trade-list.md`) と同じ規則・同じ文字列で
            出す (spec `bilateral-trade-manual-detail.md` 受渡期間セクション、
            issues #7762 / #7780 / #7782)。enum ラベルだけを出す
            TimeZoneTypeLabel だと、カスタム時間帯の契約が本モーダルでだけ
            「—」になり、一覧と同じ契約が別の時間帯区分に見える。 */}
        <DetailRow
          label={label('timeZoneType')}
          value={
            <LegTimeZoneLabel
              customBands={trade.customBands}
              timeZoneType={trade.timeZoneType}
            />
          }
        />
        <DetailRow
          label={label('periodDays')}
          value={
            periodDays !== undefined
              ? t('page:supplyBilateral.manualDetail.value.periodDaysUnit', {
                  days: periodDays,
                })
              : FALLBACK
          }
        />
        {/* AC-SBM-007: 受渡期間内の対象日を時間帯区分ごとに色分けし、受渡
            カレンダー例外 (適用日 / 除外日) を重ねた read-only カレンダー
            ビュー。 カレンダー例外の可視化はここに集約し、 契約情報セクションの
            テキスト羅列行は設けない (issue #5905、2026-08-24 PdM 判断)。 色分け
            の計算 (休日判定・時間帯区分の band 展開・例外適用) は backend
            (calendarDays) 由来で frontend では再計算しない (issue #7965)。

            calendarDays が空 = producer が「カレンダービュー表示なし」と決めた
            契約 (時間帯区分も custom_bands も持たない = 商品形態が非定型 /
            数量指定方法がコマ別、 または受渡期間が未確定)。 この時カレンダーを
            描くと受渡期間と無関係な当月グリッドが「対象日ゼロ」に見えるため、
            判定不能は判定不能として `—` に退避する (`docs/domain-terms.md`
            §時間帯区分 の「時間帯の概念を持たない契約は —」と同じ扱い)。 */}
        <DetailRow
          label={label('calendarView')}
          value={
            calendarDays.length > 0 ? (
              <DeliveryCalendarView
                calendarDays={calendarDays}
                timeZoneType={trade.timeZoneType}
              />
            ) : (
              FALLBACK
            )
          }
        />
      </Box>

      {/* ── leg 明細 ── */}
      <SectionTitle>
        {t('page:supplyBilateral.manualDetail.section.legs')}
      </SectionTitle>
      <Box borderColor="Border.lowEmphasis" borderTopWidth={1}>
        <LegDetailTable trade={trade} />
      </Box>

      {/* ── 契約情報 ── */}
      <SectionTitle>
        {t('page:supplyBilateral.manualDetail.section.contractInfo')}
      </SectionTitle>
      <Box borderColor="Border.lowEmphasis" borderTopWidth={1}>
        <DetailRow
          label={label('supplyHours')}
          value={
            supplyHoursStr === ''
              ? FALLBACK
              : t('page:supplyBilateral.manualDetail.value.supplyHoursUnit', {
                  value: supplyHoursStr,
                })
          }
        />
        <DetailRow
          label={label('energyKwh')}
          value={
            energyKwhStr === ''
              ? FALLBACK
              : t('page:supplyBilateral.manualDetail.value.energyKwhUnit', {
                  value: energyKwhStr,
                })
          }
        />
        <DetailRow
          label={label('unitPriceYenPerKwh')}
          value={
            unitPriceStr === ''
              ? FALLBACK
              : t('page:supplyBilateral.manualDetail.value.unitPriceUnit', {
                  value: unitPriceStr,
                })
          }
        />
        {/* TODO(#4787 / #4801): 「利用率(下限-上限)」行は P0 では該当データを保持しない
            ため非表示 (spec `bilateral-trade-list.md` Discussion 決定「オプション有無・
            利用率・通告タイミングの表示はすべて通告変更管理画面で扱い本画面から撤去」)。
            P1 (通告変更管理) で <DetailRow label={label('utilization')} ... /> を戻す。 */}
        <DetailRow
          label={label('settlementPriceArea')}
          value={<AreaCodeLabel areaCode={trade.settlementPriceAreaCode} />}
        />
        <DetailRow
          label={label('calendarKey')}
          value={<DeliveryCalendarKeyLabel calendarKey={trade.calendarKey} />}
        />
        <DetailRow
          label={label('ownSubmissionEntity')}
          value={
            trade.ownSubmissionEntityCode === ''
              ? FALLBACK
              : trade.ownSubmissionEntityCode
          }
        />
        <DetailRow
          label={label('partnerSubmissionEntity')}
          value={
            trade.partnerSubmissionEntityCode === ''
              ? FALLBACK
              : trade.partnerSubmissionEntityCode
          }
        />
      </Box>

      {/* ── オプション取引 (TODO(#4801): P0 非表示) ──
          オプション設定 / 日次・週次通告タイミング / 基本料金 / オプション備考の
          セクションごと P0 では出さない (#4801 PdM 確定「オプションに関する表記はカット。
          通告変更管理が入るまではいらない」/ spec `bilateral-trade-list.md` Discussion
          決定「オプション有無・利用率・通告タイミングの表示はすべて通告変更管理画面で
          扱い本画面から撤去する。優先度 P3」)。 i18n key
          (`manualDetail.section.option` / `label.option` / `label.dailyNotice` /
          `label.weeklyNotice` / `label.optionBaseFee` / `label.optionRemarks`) は残置して
          あるため、 P1 でこのセクションを戻せば復活する。
          燃調 (燃料費調整) は元から本モーダルの表示対象外 (設計書 AC-11 / 追跡 #4580)。 */}

      {/* ── 備考 ── */}
      <SectionTitle>
        {t('page:supplyBilateral.manualDetail.section.remarks')}
      </SectionTitle>
      <Box borderColor="Border.lowEmphasis" borderTopWidth={1}>
        <DetailRow
          label={label('remarks')}
          value={
            trade.remarks !== undefined && trade.remarks !== ''
              ? trade.remarks
              : t('page:supplyBilateral.manualDetail.value.remarksNone')
          }
        />
      </Box>
    </Box>
  )
}
